import { db } from "@db";
import type { GnomuxSequence } from "@db/content";
import {
  chunkNucleotides,
  DEFAULT_CHUNK_SIZE,
  encodeAminoAcids,
} from "@db/encoding";
import { iterateDecodedChunks } from "@db/iterateChunks";
import type { GnomuxFile } from "@db/objects";
import {
  computeContentHash,
  createInitialCheckpoint,
  logCollectionOperation,
} from "@db/operationLog";
import type { SequenceKind } from "@lib/genetics";

type ConcatenateSequencesParams = {
  name: string;
  sequenceIds: string[];
  spacer: string;
  ownerId: string;
  parent: string;
  collectionFileId?: string;
};

type ConcatenateSequencesResult = {
  sequenceId: string;
  fileId?: string;
};

export async function concatenateSequences({
  name,
  sequenceIds,
  spacer,
  ownerId,
  parent,
  collectionFileId,
}: ConcatenateSequencesParams): Promise<ConcatenateSequencesResult> {
  const sequences = await db.sequences.bulkGet(sequenceIds);

  const validSequences: GnomuxSequence[] = [];
  for (const seq of sequences) {
    if (!seq) {
      throw new Error("One or more sequences not found");
    }
    validSequences.push(seq);
  }

  const kind = validSequences[0].kind;
  if (!validSequences.every((s) => s.kind === kind)) {
    throw new Error("Cannot concatenate sequences of different kinds");
  }

  const parts: string[] = [];

  for (let i = 0; i < validSequences.length; i++) {
    const seq = validSequences[i];
    const chunks = iterateDecodedChunks(
      seq.id,
      seq.kind,
      seq.length,
      seq.chunkSize,
    );

    for await (const chunk of chunks) {
      parts.push(chunk);
    }

    if (spacer && i < validSequences.length - 1) {
      parts.push(spacer);
    }
  }

  const concatenated = parts.join("");

  return createConcatenatedSequence({
    name,
    sequence: concatenated,
    kind,
    ownerId,
    parent,
    collectionFileId,
  });
}

async function createConcatenatedSequence({
  name,
  sequence,
  kind,
  ownerId,
  parent,
  collectionFileId,
}: {
  name: string;
  sequence: string;
  kind: SequenceKind;
  ownerId: string;
  parent: string;
  collectionFileId?: string;
}): Promise<ConcatenateSequencesResult> {
  const sequenceId = crypto.randomUUID();
  const createdAt = new Date();
  const contentHash = await computeContentHash(sequence);

  await db.sequences.put({
    id: sequenceId,
    name,
    kind,
    length: sequence.length,
    chunkSize: DEFAULT_CHUNK_SIZE,
    isLocked: false,
    createdAt,
    ownerId,
    topology: "linear",
    contentHash,
    description: "",
    organism: "",
    taxonomy: "",
    accession: "",
    version: "",
    comment: "",
    references: [],
    customMetadata: [],
  });

  if (kind === "nt") {
    const chunks = chunkNucleotides(sequence);
    for (let i = 0; i < chunks.length; i++) {
      await db.sequenceChunks.put({
        sequenceId,
        index: i,
        data: chunks[i],
      });
    }
  } else {
    await db.sequenceChunks.put({
      sequenceId,
      index: 0,
      data: encodeAminoAcids(sequence),
    });
  }

  await createInitialCheckpoint(sequenceId, sequence, kind);

  if (collectionFileId) {
    const collectionFile = (await db.objects.get(collectionFileId)) as
      | GnomuxFile
      | undefined;

    if (collectionFile && collectionFile.type === "GnomuxSequenceCollection") {
      const existingIds = collectionFile.content as string[];
      const updatedContent = [...existingIds, sequenceId];

      await db.objects.update(collectionFileId, {
        content: updatedContent,
      } as Partial<GnomuxFile>);

      await logCollectionOperation(
        collectionFileId,
        { kind: "add_sequence", sequenceId },
        updatedContent,
      );
    }

    return { sequenceId };
  }

  const fileId = crypto.randomUUID();

  const file: GnomuxFile = {
    id: fileId,
    name,
    content: sequenceId,
    isLocked: false,
    parent,
    type: "GnomuxSequence",
    createdAt,
    ownerId,
  };

  await db.objects.put(file);

  return { sequenceId, fileId };
}
